// @givmo/app-admin — the employer SSO configuration surface, addressed by URL (AS-WEB6).
//
// ══ WHY THIS FILE EXISTS ══════════════════════════════════════════════════════════════════════
// AS-BE17 landed the whole `/admin/company/settings/sso` router (AS-BE15 its `lock-readiness`
// sibling, AS-BE18b the two SCIM routes) before the committed OpenAPI snapshot carried any of it,
// so these calls were written against the URL: `agent_docs/api-client.md` § *A whole ROUTE the
// snapshot lacks* is the sanctioned disposition — and explicitly NOT "refresh the snapshot for one
// feature", which would absorb every unrelated backend change since the last regen and collide with
// any regen in flight. `auth-policy-transport.ts` beside it is the same shape for AS-BE10.
//
// ⚠ THE SNAPSHOT HAS SINCE CAUGHT UP WITH TEN OF THE TWELVE CALLS. The 2026-09-01 refresh
// (`a33e05d7`) put all ten routes above into `packages/api-client/openapi.json` and their
// operations into `sdk.gen.ts`; this file has not graduated to them, which is a change of its own.
// The two calls the snapshot still lacks are AS-WEB25's `GET …/sso/connection`
// ({@link readSsoConnection}) and AS-WEB28's `POST …/sso/connection/start`
// ({@link startSsoConnection}), which landed after the last regen.
//
// `givmoClient` is the same `@hey-api/client-fetch` client the generated operations bind to, so
// every request here inherits the whole interceptor stack — Bearer, App Check, `X-Request-Id`
// (`wb-`), the `{data,meta,links}` unwrap, and the `{errors:[…]}` → `GivmoError` mapping. Nothing
// differs from a generated call except who wrote the URL.
//
// ══ WHAT THE FAILURES MEAN ════════════════════════════════════════════════════════════════════
// ⚠ 404 = THE SURFACE IS OFF, not "no configuration". `_require_employer_sso_enabled` is declared
// once as a ROUTER dependency, and FastAPI solves dependencies BEFORE validating a body — so while
// `EMPLOYER_SSO_ENABLED` is dark every shape of request to every route here answers 404, and a
// prober cannot tell the route exists or learn its schema. The section treats it as "not available
// in this environment", never as an error an Owner could act on.
//
// ⚠ 403 = a Member admin on a WRITE. Reads (`GET …/sso`, `GET …/sso/connection`,
// `GET …/lock-readiness`) are open to any company admin; every write is Owner-only via
// `@has_company_permission("company:edit")` on the service. The section's own Owner gate is a UI
// convenience and this is the real control.
//
// ⚠ THE GET IS THE DARK GATE for the SSO section, exactly as `GET …/auth-policy` is for the MFA
// section above it: it rides the same router dependency as the writes, so `200 ⇒ show the section,
// anything else ⇒ hide it` is the only signal the client needs, and there is no client flag that
// could fall out of step with the server's. See `agent_docs/admin-settings-tabs.md` § *Which dark
// gate* for why the probe is preferred to a `VITE_*` switch.
//
// ══ WHY THERE IS NO `metadata_xml` HERE ═══════════════════════════════════════════════════════
// `SsoConnectionIn` omits it and is `extra="forbid"`, so sending it is a 422 naming the key; the
// broker seam refuses it independently, and the service turns that into a typed
// `SSO_METADATA_XML_UNSUPPORTED` telling an admin what to send instead. {@link SsoConnectionBody}
// therefore cannot spell it — an unsupported shape is unspellable here, not merely refused.

import { givmoClient } from '@givmo/api-client';

/** The section's root route. Also its dark-reveal probe — see the header. */
export const SSO_CONFIG_URL = '/admin/company/settings/sso';
export const SSO_DOMAIN_VERIFICATION_URL = `${SSO_CONFIG_URL}/domain-verification`;
export const SSO_DOMAIN_VERIFICATION_CHECK_URL = `${SSO_DOMAIN_VERIFICATION_URL}/check`;
export const SSO_CONNECTION_URL = `${SSO_CONFIG_URL}/connection`;
export const SSO_CONNECTION_START_URL = `${SSO_CONNECTION_URL}/start`;
export const SSO_TEST_CONNECTION_URL = `${SSO_CONFIG_URL}/test-connection`;
export const SSO_LOCK_READINESS_URL = `${SSO_CONFIG_URL}/lock-readiness`;
export const SSO_POLICY_URL = `${SSO_CONFIG_URL}/policy`;
export const SSO_SCIM_URL = `${SSO_CONFIG_URL}/scim`;
export const SSO_SCIM_ROTATE_TOKEN_URL = `${SSO_SCIM_URL}/rotate-token`;

/**
 * `PUT /admin/company/settings/sso/connection` body — the employer's identity provider.
 *
 * Two accepted shapes, matching what an IT admin actually has in hand: `metadata_url` alone, or the
 * explicit trio typed out of the provider's admin console. The backend answers a typed
 * `SSO_CONNECTION_CONFIG_INVALID` for anything else, because "field required" cannot express
 * "either this one field or all three of those".
 */
export interface SsoConnectionBody {
  metadata_url?: string;
  idp_entity_id?: string;
  idp_redirect_url?: string;
  idp_certificate_pem?: string;
}

/**
 * `POST /admin/company/settings/sso/test-connection` body.
 *
 * The real ceremony's real bindings: the same PKCE challenge and the same allowlisted redirect
 * target an employee's sign-in carries. `redirect_uri` must be an EXACT match against the
 * operator-configured `EMPLOYER_SSO_ALLOWED_REDIRECT_URIS`, or the backend refuses
 * `SSO_REDIRECT_URI_NOT_ALLOWED` — the expected state in every environment until the admin origins
 * are registered.
 */
export interface SsoTestConnectionBody {
  code_challenge: string;
  redirect_uri: string;
}

/**
 * `PUT /admin/company/settings/sso/policy` body — the L0/L3/L4 tier write.
 *
 * A WHOLE-STATE restatement (both fields required), matching `CompanyUpdateAuthPolicy`: the Security
 * screen renders both controls and an Owner submitting it is stating the whole policy.
 *
 * ★ The body is `extra="forbid"` server-side, and the union is closed here for the same reason the
 * MFA body's is: a tier the backend cannot honour must be a 422, never a 200 that silently dropped
 * it. An Owner who believes they set `signin_lock` and did not is exactly the dishonest claim this
 * program exists to prevent.
 */
export interface SsoPolicyBody {
  sso_mode: 'none' | 'employer_context' | 'signin_lock';
  sso_freshness_hours: number;
}

/** What the probe learned: whether the section is live, and the config it served if so. */
export interface SsoConfigProbeResult {
  available: boolean;
  /** The response body on a 200 — the full `SsoConfigOut`. `null` otherwise. */
  body: unknown;
}

/**
 * Ask whether the employer SSO configuration surface exists in this environment.
 *
 * ⚠ KEYED ON A 200 ARRIVING, NOT ON A STATUS CODE, and deliberately so. Only a 200 reveals; every
 * other outcome hides. That covers more than the dark 404 — a 403, a 500, and a transport failure
 * all resolve the same way, because a section this client cannot confirm is live is a section it
 * must not show. It is also what lets this client land BEFORE the backend routes exist in an
 * environment: it simply hides everywhere until the server can answer.
 *
 * ⚠ SILENT. No toast, no console line, no telemetry. While the surface is dark the failure is the
 * EXPECTED answer, and logging it would put a line in every Owner's console on every visit to
 * `/settings`, in production. Same rail as the auth-policy and passkey probes.
 *
 * The body is returned undecoded: the response type is not in the snapshot either, so the tolerant
 * read belongs beside the feature (`sso-decode.ts`).
 */
export async function probeSsoConfig(): Promise<SsoConfigProbeResult> {
  try {
    const { data } = await givmoClient.get({ url: SSO_CONFIG_URL, throwOnError: true });
    return { available: true, body: data };
  } catch {
    return { available: false, body: null };
  }
}

/** Re-read the whole section. Unlike {@link probeSsoConfig} this THROWS, because by the time it is
 *  called the section is already visible and a failure is a failure rather than a dark answer. */
export async function readSsoConfig(signal?: AbortSignal): Promise<unknown> {
  const { data } = await givmoClient.get({ url: SSO_CONFIG_URL, signal, throwOnError: true });
  return data;
}

/**
 * Mint the DNS TXT token and return the record to publish.
 *
 * ⚠ RE-RUNNING CLEARS ANY EXISTING VERIFICATION and issues a new token — starting verification means
 * re-proving the domain, and a stale stamp must not keep an SSO tier alive against a proof being
 * replaced. Takes no body: the domain is the company's own `company.domain`, and accepting one from
 * a caller would be letting them choose which domain a successful check proves.
 */
export async function startDomainVerification(): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: SSO_DOMAIN_VERIFICATION_URL,
    throwOnError: true,
  });
  return data;
}

/**
 * Resolve the TXT record; on an exact match the backend stamps `domain_verified_at`.
 *
 * ⚠ A FAILED CHECK IS A TYPED 400 (`DOMAIN_VERIFICATION_FAILED`), NOT A 200 CARRYING A MESSAGE — and
 * the two failure classes the plan requires be told apart, "we looked and it is not there" versus
 * "we could not look", share that ONE error code and differ only in the MESSAGE the backend
 * composes. So the client cannot re-derive the distinction and must not try: it renders the server's
 * sentence, which already names the record and prescribes the right remedy for whichever case
 * occurred. (`check_message` on the response model is declared but never populated by any backend
 * path today; `sso-decode.ts` reads it tri-state anyway so it renders the day it starts arriving.)
 *
 * A POST rather than a GET because it WRITES on success, and because it makes an outbound network
 * call that no cache or prefetch should be free to trigger. Re-runnable by design: DNS propagates on
 * its own schedule, so the remedy for "not yet" is to press it again.
 */
export async function checkDomainVerification(): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: SSO_DOMAIN_VERIFICATION_CHECK_URL,
    throwOnError: true,
  });
  return data;
}

/** Create or update the employer's SAML connection through the broker seam. Requires a verified
 *  domain — the broker's `organization.domains` is its own login-time gate, not a label. */
export async function saveSsoConnection(body: SsoConnectionBody): Promise<unknown> {
  const { data } = await givmoClient.put({ url: SSO_CONNECTION_URL, body, throwOnError: true });
  return data;
}

/**
 * Read back the two values the employer's IT admin pastes into their identity provider (AS-WEB25).
 *
 * `GET` on the PUT's own path, answering the PUT's own shape (`SsoConnectionOut`), so the screen holds
 * the two interchangeably. The broker assigns the values per connection and the section's read never
 * carries them: `GET …/sso` is the dark-reveal probe and asks the broker nothing, so a broker outage
 * cannot hide the tab. This read asks the broker, so it can fail on its own — a typed
 * `SSO_BROKER_UNAVAILABLE` — and the screen shows that in place of the values alone.
 *
 * ⚠ NO CONNECTION IS ABSENT KEYS, NEVER NULLS. The backend drops every `None` from this response, so
 * "nothing to show yet" is `{"configured": false}` (or `{"configured": true}` with no values when the
 * broker holds none) — never a `null` a renderer could mistake for a value.
 *
 * Read-gated like the section's read: any company admin, Owner or Member. THROWS, like
 * {@link readSsoConfig}: by the time it runs the section is already visible.
 */
export async function readSsoConnection(signal?: AbortSignal): Promise<unknown> {
  const { data } = await givmoClient.get({ url: SSO_CONNECTION_URL, signal, throwOnError: true });
  return data;
}

/**
 * Get Givmo's two values for the employer's identity provider BEFORE its details exist (AS-WEB28).
 *
 * Okta's app wizard asks for the entity ID and the reply URL before it produces the metadata URL the
 * PUT above takes, so the broker creates the connection first — with no identity provider in it —
 * and the PUT later completes that same connection. Takes NO body; answers the PUT's own shape with
 * `configured: false` (the values exist; the provider's details are not saved). Idempotent: a second
 * call answers the same values. Owner-only and verified-domain-only, like the PUT.
 */
export async function startSsoConnection(): Promise<unknown> {
  const { data } = await givmoClient.post({ url: SSO_CONNECTION_START_URL, throwOnError: true });
  return data;
}

/** Start a REAL SP-initiated login for the Owner's own browser. Produces no credential, no
 *  `sso_subject` binding and no assertion stamp — a test is an assertion we read and throw away. */
export async function startTestConnection(body: SsoTestConnectionBody): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: SSO_TEST_CONNECTION_URL,
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * Read what a test asserted.
 *
 * `testId` is interpolated into the path, so it is encoded: it arrives from the server as a UUID and
 * has no business being one, but a path segment built from a value this module did not mint is one
 * `encodeURIComponent` away from being safe rather than one assumption away.
 */
export async function readTestConnection(testId: string, signal?: AbortSignal): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${SSO_TEST_CONNECTION_URL}/${encodeURIComponent(testId)}`,
    signal,
    throwOnError: true,
  });
  return data;
}

/** The L4 pre-flight counts. Read-gated like the config read, not Owner-gated like the writes —
 *  hiding the warning while showing the control would be the wrong half to gate. */
export async function readLockReadiness(signal?: AbortSignal): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: SSO_LOCK_READINESS_URL,
    signal,
    throwOnError: true,
  });
  return data;
}

/**
 * Set the employer's SSO tier and assertion-freshness window. Returns the full `SsoConfigOut`.
 *
 * ⚠ THE RESPONSE IS NOT NECESSARILY WHAT WAS SENT. A tier above `none` without a verified domain and
 * a connection is CLAMPED to `none` and the clamp is REPORTED by returning the whole config — so the
 * caller must compare what came back against what it asked for (`tierWasClamped`) rather than
 * assuming a 200 means the request was honoured. An unavailable tier is REFUSED instead
 * (`SSO_TIER_NOT_AVAILABLE`); the two guards have different shapes because one is a fact the Owner
 * can fix and the other is a fact about the deployment that they cannot.
 */
export async function saveSsoPolicy(body: SsoPolicyBody): Promise<unknown> {
  const { data } = await givmoClient.put({ url: SSO_POLICY_URL, body, throwOnError: true });
  return data;
}

// ── Directory sync (SCIM) — AS-BE18b / AS-WEB7 ──────────────────────────────────────────────────
//
// ⚠ THESE TWO ARE GATED TWICE, AND ONLY THE OUTER GATE IS OBSERVABLE. The router's
// `EMPLOYER_SSO_ENABLED` covers every route in this file; these two carry a SECOND route-level
// `EMPLOYER_SCIM_ENABLED`. Nothing serializes that inner flag — the config read above rides only the
// router gate, so it answers 200 with a full `scim` block whether or not the writes are reachable,
// and `enabled: false` means "this employer has it off", never "this deployment has it dark".
//
// So there is NO probe for the SCIM half, and one cannot be written from here: a `GET` on this
// PUT-only path answers 405, because a method mismatch resolves before route dependencies (the case
// `agent_docs/admin-settings-tabs.md` § *Which dark gate* already records). The panel therefore
// treats its FIRST WRITE as the probe and latches a 404 into the dark notice — which is the right
// shape anyway, since the flag can flip between any probe and any click.

/**
 * `PUT /admin/company/settings/sso/scim` body — the directory-sync switch.
 *
 * ★ One field, and the closed shape is the point. `SsoScimIn` is `extra="forbid"` and names no
 * directory, base URL or token, because all three are the broker's to assign — accepting one would
 * let an Owner aim a reconciliation that DELETES `company_employee` rows at a directory they do not
 * own. An unsupported shape is unspellable here, exactly as {@link SsoConnectionBody} cannot spell
 * `metadata_xml`.
 */
export interface SsoScimBody {
  enabled: boolean;
}

/**
 * Turn directory sync on or off.
 *
 * ⚠ THE TWO DIRECTIONS ARE NOT SYMMETRIC. Enabling REFUSES — a typed `SSO_DOMAIN_NOT_VERIFIED` or
 * `SSO_CONNECTION_NOT_CONFIGURED` — rather than clamping the way the tier write does, because this
 * is a single switch the Owner pressed deliberately and storing `false` for it would be a control
 * that reports success and does nothing. Disabling is UNCONDITIONAL: no domain check, no connection
 * check, no broker call, so the one act that can never fail is the one that stops a sweep deleting
 * employment rows.
 *
 * ⚠ THE RESPONSE MAY CARRY THE ONLY COPY OF A CREDENTIAL. An enable that had to create the directory
 * (or repair a tokenless one) mints a bearer token and returns it — the broker keeps only a hash, so
 * that response is the single moment the value exists anywhere. A second enable answers
 * `bearer_token: null` with `has_bearer_token: true`. Callers must not persist the body: see
 * `sso-scim-panel.tsx`, which keeps it in component state and never in the query cache.
 */
export async function saveScimSettings(body: SsoScimBody): Promise<unknown> {
  const { data } = await givmoClient.put({ url: SSO_SCIM_URL, body, throwOnError: true });
  return data;
}

/**
 * Mint a replacement bearer token for the employer's directory.
 *
 * TAKES NO BODY: the directory is resolved from the caller's own company through the broker, so
 * there is nothing to name and no way to aim this at another employer's directory.
 *
 * ⚠ NOT IDEMPOTENT, AND THAT IS WHY IT IS A POST. Each call mints a new value and immediately
 * invalidates the previous one, so a retried request is a SECOND rotation rather than a repeat of
 * the first — the employer's identity provider stops syncing until the new value is pasted in. The
 * caller confirms before sending; nothing here retries.
 *
 * ⚠ AVAILABLE WHILE SYNC IS OFF. The token authenticates the employer's identity provider to the
 * broker whether or not we are reading the directory, so an Owner who suspects it is compromised can
 * replace it without first arming a sweep. It refuses only when there is no directory at all, typed
 * `SSO_SCIM_NOT_CONFIGURED`.
 */
export async function rotateScimToken(): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: SSO_SCIM_ROTATE_TOKEN_URL,
    throwOnError: true,
  });
  return data;
}
